import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";

interface WeatherData {
  months: string[];
  minimum: string[];
  maximum: string[];
  sea: number[];
  hottestMonth: string;
  coolestMonth: string;
  warmestSeaMonth: string;
  hottestTemp: number | string;
  coolestTemp: number | string;
  warmestSeaTemp: number | string;
  hottestTempF: number | string;
  coolestTempF: number | string;
  warmestSeaTempF: number | string;
}

interface TemperatureChartProps {
  weather: WeatherData | null;
  country: string;
}
const TemperatureChart: React.FC<TemperatureChartProps> = ({
  weather,
  country,
}) => {
  const options: Highcharts.Options = {
    accessibility: { enabled: false },
    chart: {
      type: "column",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: weather ? weather.months : [],
    },
    yAxis: {
      title: {
        text: "Temperature (°C)",
      },
    },
    tooltip: {
      shared: true,
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          format: "{y}°",
        },
        pointPadding: 0,
        groupPadding: 0,
      },
      spline: {
        dataLabels: {
          enabled: true,
          format: "{y}°",
        },
      },
    },
    series: [
      {
        type: "column",
        name: "Minimum C°",
        data: weather ? weather.minimum?.map((temp) => parseInt(temp, 10)) : [],
        color: "#20c0f0",
      },
      {
        type: "column",
        name: "Maximum C°",
        data: weather ? weather.maximum?.map((temp) => parseInt(temp, 10)) : [],
        color: "#ffd34e",
      },
      {
        type: "spline",
        name: "Sea C°",
        data: weather ? weather.sea : [],
        color: "#005073",
        marker: {
          enabled: false,
        },
      },
    ],
  };

  const dynamicText = `${weather && weather.hottestMonth} is the hottest month with an average temperature of ${weather && weather.hottestTemp}°C (${weather && weather.hottestTempF}°F), and the coldest month is ${weather && weather.coolestMonth} at ${weather && weather.coolestTemp}°C (${weather && weather.coolestTempF}°F). The month with the warmest sea is ${weather && weather.warmestSeaMonth} when the average sea temperature is ${weather && weather.warmestSeaTemp}°C (${weather && weather.warmestSeaTempF}°F).`;

  return (
    <Box position="relative">
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          background: "white",
          height: "20px",
          width: "100px",
          zIndex: 99,
        }}
      />
      <Typography variant="body1" sx={{ mb: 2 }}>
        {dynamicText}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        Annual Average Temperatures {country}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The following chart shows the maximum and minimum average temperatures,
        along with the average sea surface temperature.
      </Typography>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};

export default TemperatureChart;
